// Lib
import { useCallback, useMemo, useState } from 'react';

// Utils
import { getImageEditorDataObject } from '../../../imageModalUtils';
import { normaliseImageEditorRotation } from '../../../content/utils/imageModalEditorDataUtils';
import { getPersistedImageEditorTransform } from '../../../content/utils/imageModalEditorDataAdapterUtils';

// Types
import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';

type UseImageModalV2EditorTransformSessionOptions = {
    element: ImMNElement;
    // When the crop tool shows the original source, the stored transform is the session's starting point;
    // against the edited source the session starts from identity.
    shouldUseOriginalEditorSource: boolean;
};

export type ImageModalV2SessionTransform = {
    rotation: number;
    scaleX: number;
    scaleY: number;
};

/**
 * Owns the V2 editor's rotation/flip session state: the element's stored (persisted) transform, the
 * session's starting point (stored transform against the original source, identity against the edited
 * source), the live rotation/scale values the user is editing, and the derived transforms the drawing
 * overlay and wipe frame render with.
 */
export const useImageModalV2EditorTransformSession = ({
    element,
    shouldUseOriginalEditorSource,
}: UseImageModalV2EditorTransformSessionOptions) => {
    const [storedEditorData] = useState(() => getImageEditorDataObject(element));
    const storedTransform = useMemo(() => getPersistedImageEditorTransform(storedEditorData), [storedEditorData]);
    const storedRotation = storedTransform?.rotation || 0;
    const storedScaleX = storedTransform?.scaleX || 1;
    const storedScaleY = storedTransform?.scaleY || 1;

    const initialRotation = shouldUseOriginalEditorSource ? storedRotation : 0;
    const initialScaleX = shouldUseOriginalEditorSource ? storedScaleX : 1;
    const initialScaleY = shouldUseOriginalEditorSource ? storedScaleY : 1;

    const [currentRotation, setCurrentRotation] = useState<number>(initialRotation);
    const [currentScaleX, setCurrentScaleX] = useState<number>(initialScaleX);
    const [currentScaleY, setCurrentScaleY] = useState<number>(initialScaleY);
    const [hasEditorChanges, setHasEditorChanges] = useState(false);
    const markEditorChanged = useCallback(() => setHasEditorChanges(true), []);

    const isRotatedOnSide = !!currentRotation && currentRotation !== -180;

    // The transform the cropper currently displays the image with, so the wipe frame and any re-applied
    // edits stay in the orientation the user set rather than reverting to the un-flipped source.
    const editorImageTransform = `rotate(${currentRotation}deg) scaleX(${currentScaleX}) scaleY(${currentScaleY})`;
    // The loading preview shows the source at the session's starting orientation.
    const loadingImageTransform = `rotate(${initialRotation}deg) scaleX(${initialScaleX}) scaleY(${initialScaleY})`;

    // The delta this session has applied on top of its starting point — the transform the drawing
    // overlay is path-baked through so it tracks rotate/flip live.
    const drawingSessionTransform = useMemo(
        () => ({
            rotation: normaliseImageEditorRotation(currentRotation - initialRotation),
            scaleX: currentScaleX / initialScaleX,
            scaleY: currentScaleY / initialScaleY,
        }),
        [currentRotation, currentScaleX, currentScaleY, initialRotation, initialScaleX, initialScaleY],
    );
    const hasSessionDrawingTransform =
        drawingSessionTransform.rotation !== 0 ||
        drawingSessionTransform.scaleX !== 1 ||
        drawingSessionTransform.scaleY !== 1;
    const getDrawingSessionTransform = useCallback(() => drawingSessionTransform, [drawingSessionTransform]);

    /** Applies a complete session transform in one step (used by the crop-tool source toggle). */
    const applySessionTransform = useCallback(({ rotation, scaleX, scaleY }: ImageModalV2SessionTransform) => {
        setCurrentRotation(rotation);
        setCurrentScaleX(scaleX);
        setCurrentScaleY(scaleY);
    }, []);

    /** Rewinds the session to its starting point when a genuinely new image loads into the editor. */
    const resetSessionToInitial = useCallback(() => {
        setCurrentRotation(initialRotation);
        setCurrentScaleX(initialScaleX);
        setCurrentScaleY(initialScaleY);
        setHasEditorChanges(false);
    }, [initialRotation, initialScaleX, initialScaleY]);

    return {
        storedTransform,
        storedRotation,
        storedScaleX,
        storedScaleY,
        initialRotation,
        initialScaleX,
        initialScaleY,
        currentRotation,
        currentScaleX,
        currentScaleY,
        setCurrentRotation,
        setCurrentScaleX,
        setCurrentScaleY,
        isRotatedOnSide,
        editorImageTransform,
        loadingImageTransform,
        drawingSessionTransform,
        hasSessionDrawingTransform,
        getDrawingSessionTransform,
        hasEditorChanges,
        markEditorChanged,
        applySessionTransform,
        resetSessionToInitial,
    };
};
